import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import { sendNotification, getAdminIds, getAdminEmails, sendEmail, emailSpareRequestSubmitted } from '../lib/notify';
import { fmt$, ACQUISITION_TYPES } from '../lib/utils';

export default function SparePage({ isBrowse }) {
  const { profile, isAdmin, isManager } = useAuth();
  const [spares, setSpares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAvail, setFilterAvail] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [editSpare, setEditSpare] = useState(null);
  const [form, setForm] = useState({});
  const [reqForm, setReqForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    // Query all Dallas spare pool tools — is_spare = true
    // Status is either 'available' (ready to request) or 'reserved' (mid-transaction)
    const { data } = await supabase
      .from('tools')
      .select('*, branches(name), tool_models(msrp)')
      .eq('is_spare', true)
      .order('name');
    setSpares(data || []);
    const { data: br } = await supabase.from('branches').select('*').order('name');
    setBranches(br || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = spares.filter(t => {
    const q = search.toLowerCase();
    const match = !q || [t.name, t.model_number, t.serial_number, t.description, t.condition].filter(Boolean).join(' ').toLowerCase().includes(q);
    const av = !filterAvail || t.status === filterAvail;
    return match && av;
  });

  const stats = {
    total: spares.length,
    available: spares.filter(t => t.status === 'available').length,
    reserved: spares.filter(t => t.status === 'reserved').length,
    value: spares.reduce((s, t) => {
      // Use cost if set, otherwise fall back to tool_models MSRP
      const val = parseFloat(t.cost) || parseFloat(t.tool_models?.msrp) || 0;
      return s + val;
    }, 0),
  };

  function openAdd() {
  setEditSpare(null);
  setForm({ status: 'available', is_spare: true, condition: 'in_box', acquisition_type: 'unknown' });
  setShowModal(true);
}

  function openEdit(t) {
  setEditSpare(t);
  setForm({
    name: t.name,
    model_number: t.model_number || '',
    serial_number: t.serial_number || '',
    description: t.description || '',
    cost: t.cost || '',
    condition: t.condition || 'in_box',
    status: t.status,
    acquisition_type: t.acquisition_type || 'unknown',
  });
  setShowModal(true);
}

  function openRequest(tool) {
    const isSpecific = tool && typeof tool === 'object';
    setReqForm({
      tool_id: isSpecific ? tool.id : null,
      tool_name: isSpecific ? tool.name : '',
      model_number: isSpecific ? (tool.model_number || '') : '',
      serial_number: isSpecific ? (tool.serial_number || '') : '',
      is_specific: isSpecific,
      quantity: 1,
      notes: '',
      requesting_branch_id: profile?.branch_id || '',
      requesting_branch_name: profile?.branches?.name || '',
      date_needed: '',
    });
    setShowRequest(true);
  }

  async function saveSpare() {
    if (!form.name?.trim()) return alert('Tool name is required.');
    setSaving(true);
    const hqBranch = branches.find(b => b.is_headquarters) || branches.find(b => b.id === 1);
    const payload = {
  name: form.name.trim(),
  model_number: form.model_number || null,
  serial_number: form.serial_number || null,
  description: form.description || null,
  cost: form.cost || null,
  condition: form.condition || 'in_box',
  status: 'available',
  is_spare: true,
  is_branch_spare: false,
  branch_id: hqBranch?.id || 1,
  acquisition_type: form.acquisition_type || 'unknown',
};
    if (editSpare) {
      await supabase.from('tools').update(payload).eq('id', editSpare.id);
    } else {
      await supabase.from('tools').insert({ ...payload, entered_by: profile?.id });
    }
    setSaving(false);
    setShowModal(false);
    load();
  }

  async function saveRequest() {
    if (!reqForm.tool_name?.trim()) return alert('Tool name is required.');
    setSaving(true);
    await supabase.from('spare_requests').insert({
      tool_id: reqForm.tool_id || null,
      tool_name: reqForm.tool_name,
      model_number: reqForm.model_number || null,
      serial_number: reqForm.serial_number || null,
      quantity: reqForm.is_specific ? 1 : (reqForm.quantity || 1),
      manager_name: profile?.full_name,
      requested_by: profile?.id,
      requesting_branch_id: reqForm.requesting_branch_id ? parseInt(reqForm.requesting_branch_id) : null,
      requesting_branch_name: reqForm.requesting_branch_name,
      notes: reqForm.notes || null,
      date_needed: reqForm.date_needed || null,
      status: 'pending',
    });
    if (reqForm.tool_id) {
      await supabase.from('tools').update({ status: 'reserved' }).eq('id', reqForm.tool_id);
    }
    const adminIds = await getAdminIds();
    await sendNotification(adminIds,
      `New spare request: ${reqForm.tool_name}`,
      `${profile?.full_name} at ${reqForm.requesting_branch_name} has requested ${reqForm.tool_name}.`,
      'spare_request'
    );
    const adminEmails = await getAdminEmails();
    await sendEmail(
      adminEmails,
      `New spare request: ${reqForm.tool_name}`,
      emailSpareRequestSubmitted(reqForm.tool_name, reqForm.requesting_branch_name, profile?.full_name)
    );
    setSaving(false);
    setShowRequest(false);
    alert('Request submitted successfully.');
    load();
  }

  const conditionLabel = (c) => {
    if (c === 'open_box') return 'Open-Box';
    if (c === 'used') return 'Used';
    return 'In-Box';
  };

  const conditionBadgeStyle = (c) => {
    if (c === 'open_box') return { background: '#FFF3E0', color: '#E65100', border: '1px solid #FFE0B2' };
    if (c === 'used') return { background: '#F5F5F5', color: '#616161', border: '1px solid #E0E0E0' };
    return { background: '#E8F5E9', color: '#2E7D32', border: '1px solid #C8E6C9' };
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const rq = (k, v) => setReqForm(p => ({ ...p, [k]: v }));

  return (
    <div>
      <div className="page-header">
        <div className="page-title">{isBrowse ? 'Browse spare pool' : 'Central spare pool'}</div>
        <div className="page-sub">{isBrowse ? 'Dallas HQ spare inventory — browse and request' : 'Tools held at Dallas headquarters'}</div>
      </div>

      <div className="stats-grid">
        <div className="stat-card blue"><div className="stat-label">Total spares</div><div className="stat-value">{stats.total}</div></div>
        <div className="stat-card blue"><div className="stat-label">Available</div><div className="stat-value" style={{ color: '#5C35B0' }}>{stats.available}</div></div>
        <div className="stat-card amber"><div className="stat-label">Reserved</div><div className="stat-value amber">{stats.reserved}</div></div>
        <div className="stat-card"><div className="stat-label">Spare value</div><div className="stat-value" style={{ fontSize: '18px' }}>{fmt$(stats.value)}</div></div>
      </div>

      <div className="toolbar">
        <input className="search-input" placeholder="Search spares..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="filter-select" value={filterAvail} onChange={e => setFilterAvail(e.target.value)}>
          <option value="">All availability</option>
          <option value="available">Available</option>
          <option value="reserved">Reserved</option>
        </select>
        <button className="btn btn-primary" onClick={() => openRequest(null)}>+ Request unlisted tool</button>
        {!isBrowse && isAdmin && <button className="btn btn-secondary" onClick={openAdd}>+ Add spare</button>}
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <div className="loading"><div className="spinner" />Loading...</div> : (
            <table>
              <thead>
                <tr>
                  <th>Tool name</th>
                  <th>Model #</th>
                  <th>Serial #</th>
                  <th>Condition</th>
                  <th>Cost</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="8"><div className="empty-state"><div className="empty-state-icon">📦</div><div className="empty-state-title">No spares found</div><div className="empty-state-sub">{isAdmin ? 'Add spare tools to the Dallas pool.' : 'No spares available at this time.'}</div></div></td></tr>
                ) : filtered.map(t => (
                  <tr key={t.id}>
                    <td><strong>{t.name}</strong></td>
                    <td className="text-muted">{t.model_number || '—'}</td>
                    <td className="text-muted text-sm">{t.serial_number || '—'}</td>
                    <td>
                      <span style={{
                        ...conditionBadgeStyle(t.condition),
                        borderRadius: '4px', padding: '2px 8px',
                        fontSize: '11px', fontWeight: 600,
                      }}>
                        {conditionLabel(t.condition)}
                      </span>
                    </td>
                    <td>{fmt$(t.cost) !== '—' ? fmt$(t.cost) : (t.tool_models?.msrp ? <span style={{ color: 'var(--gray-mid)', fontStyle: 'italic' }}>MSRP {fmt$(t.tool_models.msrp)}</span> : '—')}</td>
                    <td>
                      <span className={`badge ${t.status === 'reserved' ? 'badge-pending' : 'badge-spare'}`}>
                       {t.status === 'reserved' ? 'Reserved' : 'Available'}
                      </span>
                    </td>
                    <td className="text-muted truncate" style={{ maxWidth: '160px' }}>{t.description || '—'}</td>
                    <td>
                      <div className="flex gap-1">
                        {t.status === 'reserved'
                          ? <span style={{ fontSize: '12px', color: 'var(--gray-lt)' }}>Reserved</span>
                          : <button className="btn btn-primary btn-sm" onClick={() => openRequest(t)}>Request</button>
                        }
                        {!isBrowse && isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}>Edit</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── ADD / EDIT SPARE MODAL ── */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editSpare ? 'Edit spare' : 'Add spare'}</span>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Tool name *</label><input className="form-input" value={form.name || ''} onChange={e => f('name', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Model number</label><input className="form-input" value={form.model_number || ''} onChange={e => f('model_number', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Serial number</label><input className="form-input" value={form.serial_number || ''} onChange={e => f('serial_number', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Cost ($)</label><input className="form-input" type="number" min="0" step="0.01" value={form.cost || ''} onChange={e => f('cost', e.target.value)} /></div>
                <div className="form-group">
  <label className="form-label">
    How was this acquired?
    <span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px', fontWeight: 400 }}>affects savings reporting</span>
  </label>
  <select className="form-select" value={form.acquisition_type || 'unknown'} onChange={e => f('acquisition_type', e.target.value)}>
    {ACQUISITION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
  </select>
</div>
                <div className="form-group">
                  <label className="form-label">Condition</label>
                  <select className="form-select" value={form.condition || 'in_box'} onChange={e => f('condition', e.target.value)}>
                    <option value="in_box">In-Box (new)</option>
                    <option value="open_box">Open-Box</option>
                    <option value="used">Used</option>
                  </select>
                </div>
                <div className="form-group full"><label className="form-label">Notes</label><textarea className="form-textarea" value={form.description || ''} onChange={e => f('description', e.target.value)} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveSpare} disabled={saving}>{saving ? 'Saving...' : 'Save spare'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── REQUEST MODAL ── */}
      {showRequest && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowRequest(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{reqForm.is_specific ? 'Request this tool' : 'Request unlisted tool'}</span>
              <button className="close-btn" onClick={() => setShowRequest(false)}>×</button>
            </div>
            <div style={{ background: '#F8F8F8', padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
              Requesting as: <strong>{profile?.full_name}</strong> · {profile?.branches?.name || 'All locations'}
            </div>
            <div className="modal-body">
              {!reqForm.is_specific && (
                <div className="alert alert-info" style={{ marginBottom: '1rem', fontSize: '12px' }}>
                  Use this form to request a tool not currently in the Dallas spare pool.
                </div>
              )}
              <div className="form-grid">
                <div className="form-group full">
                  <label className="form-label">Tool name *</label>
                  <input className="form-input" value={reqForm.tool_name} onChange={e => !reqForm.is_specific && rq('tool_name', e.target.value)} readOnly={reqForm.is_specific} style={reqForm.is_specific ? { background: 'var(--off-white)', color: 'var(--gray-mid)' } : {}} />
                </div>
                {reqForm.is_specific && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Model #</label>
                      <input className="form-input" value={reqForm.model_number || '—'} readOnly style={{ background: 'var(--off-white)', color: 'var(--gray-mid)' }} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Serial #</label>
                      <input className="form-input" value={reqForm.serial_number || '—'} readOnly style={{ background: 'var(--off-white)', color: 'var(--gray-mid)' }} />
                    </div>
                  </>
                )}
                {!reqForm.is_specific && (
                  <div className="form-group">
                    <label className="form-label">Quantity</label>
                    <input className="form-input" type="number" min="1" value={reqForm.quantity} onChange={e => rq('quantity', e.target.value)} />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Requesting branch</label>
                  <select className="form-select" value={reqForm.requesting_branch_id} onChange={e => { const b = branches.find(br => br.id === parseInt(e.target.value)); rq('requesting_branch_id', e.target.value); rq('requesting_branch_name', b?.name || ''); }}>
                    <option value="">Select branch</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date needed by</label>
                  <input className="form-input" type="date" value={reqForm.date_needed || ''} onChange={e => rq('date_needed', e.target.value)} />
                </div>
                <div className="form-group full">
                  <label className="form-label">Notes / reason</label>
                  <textarea className="form-textarea" value={reqForm.notes} onChange={e => rq('notes', e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowRequest(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveRequest} disabled={saving}>{saving ? 'Submitting...' : 'Submit request'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}